/**
 * shared/onboarding.ts - the onboarding chat's state and the rules the server and the client share (2026-10-08).
 *
 * The state lives in workspaces.onboarding (JSONB, nullable, added at startup by server/onboarding.ts ensureOnboardingSchema):
 *   null                         a workspace made before the marker existed: finished, unless it is still the bare
 *                                "My Business" placeholder an abandoned onboarding chat left behind
 *   { status: "in_progress" }    the chat started; the app shows the chat again on reload until it completes
 *   { status: "complete" }       finished; completedAt says when, followUps what ran in the background afterwards
 *
 * Pure functions only (no I/O), so the resume gate can be tested on its own (scripts/regression/onboarding.mts).
 */

export const ONBOARDING_GOALS = {
  leads: { label: "More enquiries and booked calls", nurtureGoal: "appointment" },
  sales: { label: "More sales", nurtureGoal: "appointment" },
  awareness: { label: "Get known in our market", nurtureGoal: "awareness" },
} as const;
export type OnboardingGoal = keyof typeof ONBOARDING_GOALS;

/** The channels the chat offers. Google is not offered (not supported yet). "blog" = the business's own website blog. */
export const ONBOARDING_CHANNELS = ["linkedin", "meta", "x", "blog"] as const;
export type OnboardingChannel = (typeof ONBOARDING_CHANNELS)[number];
export const ONBOARDING_CHANNEL_LABELS: Record<OnboardingChannel, string> = {
  linkedin: "LinkedIn",
  meta: "Instagram / Facebook",
  x: "X",
  blog: "Website blog",
};
/** The social channels among them (each has a connect link). */
export const SOCIAL_ONBOARDING_CHANNELS: readonly OnboardingChannel[] = ["linkedin", "meta", "x"];

export interface OnboardingAnswers {
  goal?: OnboardingGoal;
  /** the owner's own words about the goal, when they added detail */
  goalNote?: string;
  /** names or websites as the owner wrote them; [] = they had none or skipped */
  competitors?: string[];
  /** [] = none for now */
  channels?: OnboardingChannel[];
  challenge?: string;
}

export type OnboardingFollowUpKey = "brand" | "knowledge" | "strategy" | "harry";
export interface OnboardingFollowUpResult {
  status: "done" | "skipped" | "failed";
  at: string;
  detail?: string;
}

export interface OnboardingState {
  status: "in_progress" | "complete";
  startedAt?: string;
  completedAt?: string;
  answers?: OnboardingAnswers;
  followUps?: Partial<Record<OnboardingFollowUpKey, OnboardingFollowUpResult>>;
}

type WsLike = { name?: string | null; brandName?: string | null; userId?: string | null; onboarding?: unknown };

export function isPlaceholderName(v: unknown): boolean {
  const s = typeof v === "string" ? v.trim().toLowerCase() : "";
  return s === "" || s === "my business";
}

export function onboardingStateOf(ws: { onboarding?: unknown } | null | undefined): OnboardingState | null {
  const st = ws?.onboarding;
  if (!st || typeof st !== "object" || Array.isArray(st)) return null;
  return st as OnboardingState;
}

/**
 * Has this workspace finished onboarding? With a marker: its status. Without one (made before the marker existed): finished,
 * unless it is still the bare "My Business" placeholder with no real brand name (an onboarding chat abandoned half-way).
 */
export function isOnboardingComplete(ws: WsLike): boolean {
  const st = onboardingStateOf(ws);
  if (st) return st.status === "complete" || !!st.completedAt;
  return !(isPlaceholderName(ws.name) && isPlaceholderName(ws.brandName));
}

/**
 * Whether the signed-in user sees the onboarding chat instead of the app: no workspace at all, or their only workspace is
 * their own and has not finished onboarding (a reload mid-chat resumes the chat). A workspace someone else owns (an invited
 * team member) or a user with several workspaces never sees it.
 */
export function shouldShowOnboarding(workspaces: WsLike[] | null | undefined, userId: string | null | undefined): boolean {
  if (!Array.isArray(workspaces)) return false;
  if (workspaces.length === 0) return true;
  if (workspaces.length !== 1) return false;
  const only = workspaces[0];
  if (userId && only.userId && only.userId !== userId) return false;
  return !isOnboardingComplete(only);
}

/** "leads" / "sales" / "awareness", or the owner's words for one of them. Anything else: null. */
export function normalizeGoal(v: unknown): OnboardingGoal | null {
  const s = typeof v === "string" ? v.trim().toLowerCase() : "";
  if (!s) return null;
  if (s in ONBOARDING_GOALS) return s as OnboardingGoal;
  if (/\b(enquir|inquir|lead|call|booking|booked|appointment|demo|consult|meeting|sign-?ups?|trials?)\w*/.test(s)) return "leads";
  if (/\b(sale|sell|revenue|order|purchase|customers? buy)\w*/.test(s)) return "sales";
  if (/\b(known|awareness|brand|visib|reach|audience|followers?|recogni)\w*/.test(s)) return "awareness";
  return null;
}

const CHANNEL_ALIASES: Record<string, OnboardingChannel> = {
  linkedin: "linkedin", "linked in": "linkedin",
  meta: "meta", instagram: "meta", facebook: "meta", ig: "meta", fb: "meta", "instagram/facebook": "meta", "instagram / facebook": "meta",
  x: "x", twitter: "x", "x (twitter)": "x",
  blog: "blog", website: "blog", "website blog": "blog", wordpress: "blog", "our blog": "blog", "my blog": "blog",
};

/** The chosen channels in a fixed order, deduplicated. Google and anything unknown are dropped. Accepts a list or "a, b and c". */
export function normalizeChannels(v: unknown): OnboardingChannel[] {
  const raw: string[] = Array.isArray(v) ? v.map((x) => String(x ?? "")) : typeof v === "string" ? v.split(/,|;|\band\b|\n|\//i) : [];
  const out = new Set<OnboardingChannel>();
  for (const r of raw) {
    const k = r.trim().toLowerCase().replace(/\s+/g, " ");
    if (!k) continue;
    const hit = CHANNEL_ALIASES[k] ?? (Object.entries(CHANNEL_ALIASES).find(([alias]) => alias.length > 2 && k.includes(alias))?.[1]);
    if (hit) out.add(hit);
  }
  return ONBOARDING_CHANNELS.filter((c) => out.has(c));
}

/** workspaces.channel_preferences for the chosen channels: Google always off; the blog choice is kept next to the social switches. */
export function channelPreferencesFor(channels: readonly OnboardingChannel[]): { linkedin: boolean; x: boolean; meta: boolean; google: boolean; blog: boolean } {
  const has = (c: OnboardingChannel) => channels.includes(c);
  return { linkedin: has("linkedin"), x: has("x"), meta: has("meta"), google: false, blog: has("blog") };
}

const NONE_RE = /^(none|no|nope|n\/a|na|skip|not sure|no idea|nobody|no one|don'?t know|idk|-)\.?$/i;

/** Competitor names / sites as the owner wrote them: trimmed, "none"/"skip" dropped, deduplicated (case-insensitive), at most 10. */
export function normalizeCompetitors(v: unknown): string[] {
  const raw: string[] = Array.isArray(v) ? v.map((x) => String(x ?? "")) : typeof v === "string" ? v.split(/,|;|\n/) : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of raw) {
    const s = r.replace(/\s+/g, " ").trim().replace(/^(and|or)\s+/i, "").slice(0, 120);
    if (!s || NONE_RE.test(s)) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
    if (out.length >= 10) break;
  }
  return out;
}

/** What the chat still has to ask, in order. "business" = the name or the industry is not saved yet. */
export function remainingOnboardingSteps(ws: WsLike & { industry?: string | null }): Array<"business" | "goal" | "competitors" | "channels"> {
  const a = onboardingStateOf(ws)?.answers ?? {};
  const out: Array<"business" | "goal" | "competitors" | "channels"> = [];
  if (isPlaceholderName(ws.brandName) || !String(ws.industry ?? "").trim()) out.push("business");
  if (!a.goal) out.push("goal");
  if (a.competitors === undefined) out.push("competitors");
  if (a.channels === undefined) out.push("channels");
  return out;
}

/** Whether a social channel is connected: the workspace's integrations flag (same rule as the content studio) or a stored OAuth token. */
export function channelConnected(integrations: unknown, tokenPlatforms: readonly string[], channel: OnboardingChannel): boolean {
  const integ = (integrations && typeof integrations === "object" ? integrations : {}) as Record<string, { connected?: boolean } | undefined>;
  const tok = new Set(tokenPlatforms.map((p) => String(p).toLowerCase()));
  if (channel === "meta") return !!(integ.meta?.connected || integ.instagram?.connected || integ.facebook?.connected || integ.instagram_business?.connected)
    || ["meta", "facebook", "instagram", "instagram_business"].some((p) => tok.has(p));
  if (channel === "x") return !!integ.x?.connected || tok.has("x") || tok.has("twitter");
  if (channel === "linkedin") return !!integ.linkedin?.connected || tok.has("linkedin");
  return false;
}

/** One line of the "Finish setup" card on Draper home (GET /api/workspaces/:wsId/setup-status). */
export interface SetupItem {
  key: string;
  label: string;
  done: boolean;
  /** the exact place in the app where it is done */
  href: string;
  detail?: string;
}
